import { useSyncExternalStore } from "react";
import type { CockpitLayout, CockpitView } from "@kundeninfo/api-client";
import { createLayoutStore, mergeLayoutBy, perClient, useLayoutHolder } from "../customerOverview/tileLayout.js";
import type { WriteKind } from "../customerOverview/tileLayout.js";
import type { CockpitClient } from "./cockpitClient.js";

const TILE_ID_PREFIX = "ck-tile-";
const keyOfTile = (id: string): string | null => (id.startsWith(TILE_ID_PREFIX) ? id.slice(TILE_ID_PREFIX.length) : null);

/** Was die Wand meldet; die bewegte Kachel (`id`) bleibt beim Halten der Lage aus dem Spiel. */
type WallChange = { reihenfolge: readonly string[]; ausgeblendet: readonly string[]; id?: string };

/** `layout` und `newTiles` sind `null`, bis der Benutzer etwas getan hat: Bis dahin gilt der Abruf. */
type StoreState = { layout: CockpitLayout | null; newTiles: readonly string[] | null; error: WriteKind | null };
type Current = Pick<CockpitView, "layout"> & { newTiles: readonly string[] };

/**
 * Was hinausgeht: nur, was der Katalog dieses Tabs kennt und was nicht neu ist. Eine Kachel, die der Benutzer noch nicht gewählt hat, fehlt ganz;
 * so bleibt sie „neu“ (K7, 648). Das gilt auch für eine, die erst die Antwort des Servers mitbrachte (älterer Tab nach einem Deploy): Sie stand nie im Dialog.
 */
const sendable = (layout: CockpitLayout, catalog: CockpitView["catalog"], newTiles: readonly string[]): CockpitLayout => {
  const known = (key: string): boolean => catalog.some((d) => d.tileKey === key) && !newTiles.includes(key);
  return { order: layout.order.filter(known), hidden: layout.hidden.filter(known),
    settings: Object.fromEntries(Object.entries(layout.settings).filter(([key]) => known(key))) };
};

/** Der Speicher der Cockpit-Anordnung eines Benutzers: ein Speicher je Klient, Schlange und Stand wie in der Übersicht (`createLayoutStore`). */
function createStore(client: CockpitClient) {
  const { update, write, isBusy, ...store } = createLayoutStore<StoreState>({ layout: null, newTiles: null, error: null });
  const currentOf = (view: CockpitView): Current => {
    const { layout, newTiles } = store.getState();
    return { layout: layout ?? view.layout, newTiles: newTiles ?? view.newTiles };
  };
  return {
    ...store,
    currentOf,
    arrange(view: CockpitView, change: WallChange): void {
      const { layout, newTiles } = currentOf(view);
      const next = { ...layout, ...mergeLayoutBy(layout, change, keyOfTile) };
      update({ layout: next }, change.id);
      void write("save", async () => ({ layout: await client.saveCockpitLayout(sendable(next, view.catalog, newTiles)) }));
    },
    // Ohne Zeile gibt es keine neuen Kacheln (K7).
    reset(): void { void write("reset", async () => ({ layout: await client.resetCockpitLayout(), newTiles: [] })); },
    /**
     * Die Kachelwahl: Erst die Antwort ändert die Wand, ein Fehler gehört in den offenen Dialog. Hat die Wahl eine Umordnung überholt, ist
     * die nach einem Fehler nur noch hier gezeichnet: Das sagt die Warnung der Wand.
     */
    async saveChoice(view: CockpitView, draft: CockpitLayout): Promise<void> {
      const overtaken = isBusy();
      const answered = await write(null, async () => ({ layout: await client.saveCockpitLayout(sendable(draft, view.catalog, [])), newTiles: [] }));
      if (answered) return;
      if (overtaken) update({ error: "save" });
      throw new Error("cockpit.layout");
    },
  };
}

const storeOf = perClient(createStore);

/**
 * Die Anordnung des Cockpits: Sie folgt dem Abruf, bis der Benutzer etwas ändert; dann gilt sein Stand bis zum Neuladen der Seite. Umordnen
 * speichert sofort und sendet die neuen Schlüssel nicht mit, nur `saveChoice` tut das und beendet damit „neu“ (648). `beforeReplace` läuft
 * unmittelbar vor jedem Wechsel des Stands oder der Meldung (`changedId`: die bewegte Kachel, sonst keine); der Aufrufer merkt sich dort die Lage im Bild.
 */
export function useCockpitLayout(client: CockpitClient, view: CockpitView, beforeReplace?: (changedId?: string) => void): {
  layout: CockpitLayout; newTiles: readonly string[]; error: WriteKind | null;
  arrange: (change: WallChange) => void; reset: () => void; saveChoice: (draft: CockpitLayout) => Promise<void>;
} {
  const store = storeOf(client);
  const { error } = useSyncExternalStore(store.subscribe, store.getState);
  useLayoutHolder(store, beforeReplace);
  return { ...store.currentOf(view), error, arrange: (change) => { store.arrange(view, change); }, reset: store.reset, saveChoice: (draft) => store.saveChoice(view, draft) };
}
